import React from 'react';
import { Badge, Box, Flex, Text } from '@chakra-ui/react';
import { CaretRight, Warning } from 'phosphor-react';

import { GlassCard } from '../../../../../../components/glass';
import type { IEmpresaSaude } from '../../../../../../hooks/useContaCilEmpresas';
import { corConfianca, fmtDoc, fmtValor } from '../../conciliar/shared';

function iniciais(nome?: string | null): string {
  if (!nome) return '??';
  const ps = nome.trim().split(/\s+/).filter(Boolean);
  if (ps.length === 0) return '??';
  if (ps.length === 1) return ps[0].slice(0, 2).toUpperCase();
  return (ps[0][0] + ps[ps.length - 1][0]).toUpperCase();
}

export function EmpresaSaudeCard({ item, onClick }: { item: IEmpresaSaude; onClick: () => void }) {
  const nome = item.razao_social || item.nome || `Empresa ${item.empresa_id}`;
  const pct = item.percent_conciliado;
  const corBarra = corConfianca(pct);
  const emDia = item.pendentes === 0;

  return (
    <GlassCard
      intensity="medium"
      hoverable
      p={4}
      as="button"
      w="full"
      textAlign="left"
      cursor="pointer"
      onClick={onClick}
    >
      <Flex align="flex-start" gap={3}>
        <Flex
          w="44px"
          h="44px"
          flexShrink={0}
          borderRadius="xl"
          align="center"
          justify="center"
          bgGradient="linear(135deg, #EDE9FE 0%, #DDD6FE 100%)"
          color="#7C3AED"
          fontWeight="700"
          fontSize="14px"
        >
          {iniciais(nome)}
        </Flex>

        <Box flex={1} minW={0}>
          <Flex align="center" justify="space-between" gap={2} mb={0.5}>
            <Text fontSize="14px" fontWeight="700" color="#0F172A" isTruncated title={nome}>
              {nome}
            </Text>
            <Flex align="center" gap={1.5} flexShrink={0}>
              {item.inativa && (
                <Badge colorScheme="gray" borderRadius="full" fontSize="9px">
                  inativa
                </Badge>
              )}
              <Badge colorScheme={emDia ? 'green' : 'orange'} borderRadius="full" fontSize="10px">
                {emDia ? 'em dia' : 'pendências'}
              </Badge>
              <Box color="#CBD5E1">
                <CaretRight size={14} weight="bold" />
              </Box>
            </Flex>
          </Flex>

          <Text fontSize="11px" color="#94A3B8" mb={2} isTruncated>
            {item.external_id ? `id ${item.external_id} · ` : ''}
            {fmtDoc(item.cnpj) || '—'}
            {item.regime ? ` · ${item.regime}` : ''}
          </Text>

          <Flex align="center" gap={2} mb={1.5}>
            <Box h="6px" flex={1} borderRadius="full" bg="rgba(15,23,42,0.06)" overflow="hidden">
              <Box h="full" w={`${Math.min(100, pct)}%`} borderRadius="full" bg={corBarra} />
            </Box>
            <Text fontSize="11px" fontWeight="700" color={corBarra} minW="38px" textAlign="right">
              {pct.toFixed(0)}%
            </Text>
          </Flex>

          <Flex align="center" gap={3} wrap="wrap">
            <Text fontSize="11px" color="#475569">
              <Text as="span" fontWeight="700" color="#B45309">
                R$ {fmtValor(item.valor_pendente)}
              </Text>{' '}
              pendente
            </Text>
            <Text fontSize="11px" color="#64748B">
              {item.qtd_contas} {item.qtd_contas === 1 ? 'conta' : 'contas'}
            </Text>
            {item.qtd_sem_poc > 0 && (
              <Flex align="center" gap={1} color="#B45309">
                <Warning size={12} weight="fill" />
                <Text fontSize="11px" fontWeight="600">
                  {item.qtd_sem_poc} sem POC
                </Text>
              </Flex>
            )}
          </Flex>
        </Box>
      </Flex>
    </GlassCard>
  );
}
